import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { appstr } from "@coreapp/utils/appstr";
import {
  Accordion,
  addOrRemove,
  Flex,
  List,
  ListItemOption,
  Search,
  Switch,
  Text
} from "@theorgchartdev/storybook";
import { useEffect, useRef, useState } from "react";
import { FieldLabelComposite } from "@/appComponents/Blocks/Composites/FieldLabelComposite/FieldLabelComposite";
import cx from "classnames";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { usePlanSettingsContext } from "@/providers/PlanSettingsProvider";
import { usePlanFields } from "./usePlanFields";
import { PLAN_GENERAL_TEST_PREFIX } from "./Utils";
import { SearchHandleRef } from "@theorgchartdev/storybook/dist/components/Search/Search";

export const General = () => {
  const { chartConfig, settings, updateSettings, getMockWsMgr } = usePlanSettingsContext();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchInputRef = useRef<SearchHandleRef>(null);

  const isPosChart = IsPositionChart(chartConfig ?? {});
  const mockDoc = getMockWsMgr()?.GetDoc();

  const { requiredHolderFields, requiredPositionFields, filteredFieldOptions } = usePlanFields({
    isPosChart,
    mockDoc,
    searchQuery
  });

  // Clear searchQuery when base chart selection changes
  useEffect(() => {
    if (!chartConfig) {
      return;
    }
    searchInputRef.current?.setValue("");
    setSearchQuery("");
  }, [chartConfig]);

  const selectedFields = {
    holder: settings?.additionalFields?.holder ?? [],
    position: settings?.additionalFields?.position ?? []
  };

  const handleQueryChange = (query: string) => {
    setSearchQuery(query);
  };

  const buildFieldElements = (type: "holder" | "position" = "holder"): ListItemOption[] => {
    const selectedFieldNames = [...(selectedFields[type] ?? [])];
    const requiredFieldNames = type === "position" ? requiredPositionFields : requiredHolderFields;
    const checkedFieldsSet = new Set(requiredFieldNames.concat(selectedFieldNames));

    const onChange = (fieldName: string) => {
      const newSelection = addOrRemove(selectedFieldNames, fieldName);
      const newSelectedFields = {
        ...selectedFields,
        [type]: newSelection
      };
      updateSettings({
        additionalFields: {
          ...(settings?.additionalFields ?? {}),
          ...newSelectedFields
        }
      });
    };

    const baseClass = "ocn-field-toggle-option";
    return filteredFieldOptions[type].map(
      ({ fName: localizedName, fValue: fieldName, isDisabled }) => {
        const checked = checkedFieldsSet.has(fieldName);
        const checkedClass = checked && `${baseClass}-label--checked`;

        return {
          content: (
            <span className={baseClass}>
              <span className={`${baseClass}-switch`}>
                <Switch
                  id={`additional-plan-fields-${type}-${fieldName}`}
                  onChange={() => onChange(fieldName)}
                  checked={checked}
                  disabled={isDisabled}
                />
              </span>
              <FieldLabelComposite
                className={cx(`${baseClass}-label`, checkedClass)}
                name={localizedName}
                size={"small"}
              />
            </span>
          ),
          clickHandler: (_, e) => {
            if (isDisabled) {
              return;
            }
            e?.preventDefault();
            onChange(fieldName);
          },
          label: fieldName,
          dataTestId: `${PLAN_GENERAL_TEST_PREFIX}toggle-${type}-${fieldName}`,
          value: fieldName
        };
      }
    );
  };

  const options = isPosChart
    ? { holder: buildFieldElements("holder"), position: buildFieldElements("position") }
    : { holder: buildFieldElements("holder"), position: [] };

  return (
    <SectionCardGroup>
      <SectionCard
        title={appstr("Default editable fields")}
        titleContent={
          <Text size="small" type="body" element="p">
            {appstr(
              "Choose the default editable fields that you want to show in the {entity} modals.",
              {
                entity: isPosChart
                  ? `${appstr("Employee")} ${appstr("and")} ${appstr("Position")}`.toLowerCase()
                  : appstr("Employee").toLowerCase()
              }
            )}
          </Text>
        }
        titleContainerProps={{
          element: "h4",
          modifier: "bold",
          size: "xsmall",
          style: { marginBottom: 0 }
        }}
        headerFlexProps={{
          direction: "column",
          alignItems: "flex-start",
          justifyContent: "unset"
        }}
        flexProps={{
          height: "100%"
        }}
        childWrapperClass={{
          padding: "4px 4px 0",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          flex: "1",
          height: "100%",
          overflow: "hidden"
        }}
      >
        <div className="ocn-search-wrapper-full-width">
          <Search
            clearable
            dataTestId={`${PLAN_GENERAL_TEST_PREFIX}field-search`}
            inputProps={{
              disabled: false
            }}
            leftIcon
            onInput={handleQueryChange}
            placeholder={appstr("Search")}
            size="medium"
            timeout={200}
            handleRef={searchInputRef}
          />
        </div>
        <Flex direction="column" gap="16px" className="ocn-list-scrollbar" overflow="auto" flex={1}>
          {isPosChart ? (
            <>
              {options.holder.length > 0 && (
                <Accordion
                  dataTestId={`${PLAN_GENERAL_TEST_PREFIX}employee-fields-accordion`}
                  name={
                    <Text size={"medium"} modifier={"bold"}>
                      {appstr("Employee fields")}
                    </Text>
                  }
                  initExpand
                  iconPosition="right"
                  labelProps={{
                    backgroundColor: "orange",
                    hoverColor: "orange",
                    customStyle: { justifyContent: "space-between", padding: "12px" }
                  }}
                  beforeContent={
                    <List
                      enableVirtualization={false}
                      options={options.holder}
                      listWidth="fill"
                      optionSize={"small"}
                      showCheck={false}
                    />
                  }
                />
              )}
              {options.position.length > 0 && (
                <Accordion
                  dataTestId={`${PLAN_GENERAL_TEST_PREFIX}position-fields-accordion`}
                  name={
                    <Text size={"medium"} modifier={"bold"}>
                      {appstr("Position fields")}
                    </Text>
                  }
                  initExpand={true}
                  iconPosition="right"
                  labelProps={{
                    backgroundColor: "orange",
                    hoverColor: "orange",
                    customStyle: { justifyContent: "space-between", padding: "12px" }
                  }}
                  beforeContent={
                    <List
                      enableVirtualization={false}
                      options={options.position}
                      listWidth="fill"
                      optionSize={"small"}
                      showCheck={false}
                    />
                  }
                />
              )}
            </>
          ) : (
            <List
              enableVirtualization={false}
              options={options.holder}
              listWidth="fill"
              optionSize={"small"}
              showCheck={false}
            />
          )}
        </Flex>
      </SectionCard>
    </SectionCardGroup>
  );
};
